import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { buildApiUrl, TESS_API_URL } from '@core/http/api.config';
import { SKIP_AUTH } from '@core/interceptors/skip-auth.context-token';
import { AuthenticationData } from '@core/models/auth/authentication-data.interface';
import { AuthenticateResponse } from '@core/models/auth/authenticate-response.interface';
import { catchError, map, Observable, of, switchMap, tap } from 'rxjs';
import { TemporaryAuthPayload } from '@core/models/auth/temprorary-auth-payload.interface';

const TEMPORARY_AUTH_TIME_ZONE = 'America/Denver';
const TOKEN_EXPIRATION_CLOCK_SKEW_MS = 30_000;

const temporaryAuthPayload: Readonly<TemporaryAuthPayload> = {
  client_id: 'ngAuthApp',
  companyshortname: 'happy',
  grant_type: 'password',
  password: 'test',
  username: 'donna',
};

@Injectable({ providedIn: 'root' })
export class AuthService {
  readonly #httpClient = inject(HttpClient);
  readonly #tessApiUrl = inject(TESS_API_URL);

  protected readonly AUTH_STORAGE_KEY = 'authenticationData';
  protected readonly APPLICATION_CONFIGURATION_NOCACHE = '1618839289922';

  readonly #authenticationDataState = signal<AuthenticationData | null>(this.#readAuthenticationData());

  readonly authenticationData = this.#authenticationDataState.asReadonly();
  readonly accessToken = computed<string | null>(() => {
    const authenticationData = this.#authenticationDataState();

    return this.#isAuthenticationDataActive(authenticationData) ? authenticationData.accessToken : null;
  });
  readonly isAuthenticated = computed<boolean>(() => this.accessToken() !== null);

  readonly #skipAuthContext = new HttpContext().set(SKIP_AUTH, true);

  initializeTemporaryAuthentication(): Observable<void> {
    if (this.isAuthenticated()) {
      return of(void 0);
    }

    return this.#loadApplicationConfiguration().pipe(
      switchMap(() => this.authenticateTemporaryUser()),
      tap(authenticationData => this.setAuthenticationData(authenticationData)),
      map(() => void 0),
      catchError(() => {
        this.clearAuthenticationData();

        return of(void 0);
      }),
    );
  }

  authenticateTemporaryUser(): Observable<AuthenticationData> {
    const params = new HttpParams().set('iana', TEMPORARY_AUTH_TIME_ZONE);

    return this.#httpClient
      .post<AuthenticateResponse>(buildApiUrl(this.#tessApiUrl, '/api/Account/authenticate'), temporaryAuthPayload, {
        context: this.#skipAuthContext,
        params,
      })
      .pipe(
        map(response => {
          return {
            accessToken: response.access_token,
            isExpired: false,
            refreshToken: response.refresh_token,
            timeZone: TEMPORARY_AUTH_TIME_ZONE,
          } satisfies AuthenticationData;
        }),
      );
  }

  setAuthenticationData(authenticationData: AuthenticationData): void {
    this.#authenticationDataState.set(authenticationData);
    localStorage.setItem(this.AUTH_STORAGE_KEY, JSON.stringify(authenticationData));
  }

  clearAuthenticationData(): void {
    this.#authenticationDataState.set(null);
    localStorage.removeItem(this.AUTH_STORAGE_KEY);
  }

  getAccessToken(): string | null {
    const authenticationData = this.#authenticationDataState();

    if (this.#isAuthenticationDataActive(authenticationData)) {
      return authenticationData.accessToken;
    }

    if (authenticationData) {
      this.clearAuthenticationData();
    }

    return null;
  }

  #loadApplicationConfiguration(): Observable<void> {
    const params = new HttpParams().set('nocache', this.APPLICATION_CONFIGURATION_NOCACHE);

    return this.#httpClient.get<void>(buildApiUrl(this.#tessApiUrl, '/api/ApplicationConfiguration'), {
      context: this.#skipAuthContext,
      params,
    });
  }

  #readAuthenticationData(): AuthenticationData | null {
    const rawValue = localStorage.getItem(this.AUTH_STORAGE_KEY);

    if (!rawValue) {
      return null;
    }

    try {
      const parsedValue = JSON.parse(rawValue) as Partial<AuthenticationData> | null;

      if (!parsedValue?.accessToken || !parsedValue.refreshToken || !parsedValue.timeZone) {
        return null;
      }

      const authenticationData = {
        accessToken: parsedValue.accessToken,
        isExpired: parsedValue.isExpired ?? false,
        refreshToken: parsedValue.refreshToken,
        timeZone: parsedValue.timeZone,
      };

      if (!this.#isAuthenticationDataActive(authenticationData)) {
        localStorage.removeItem(this.AUTH_STORAGE_KEY);

        return null;
      }

      return authenticationData;
    } catch {
      return null;
    }
  }

  #isAuthenticationDataActive(authenticationData: AuthenticationData | null): authenticationData is AuthenticationData {
    return (
      !!authenticationData?.accessToken &&
      authenticationData.isExpired === false &&
      !this.#isAccessTokenExpired(authenticationData.accessToken)
    );
  }

  #isAccessTokenExpired(accessToken: string): boolean {
    const expiresAt = this.#readTokenExpirationTime(accessToken);

    return expiresAt === null || expiresAt <= Date.now() + TOKEN_EXPIRATION_CLOCK_SKEW_MS;
  }

  #readTokenExpirationTime(accessToken: string): number | null {
    const payload = accessToken.split('.')[1];

    if (!payload) {
      return null;
    }

    try {
      const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/');
      const paddedPayload = normalizedPayload.padEnd(
        normalizedPayload.length + ((4 - (normalizedPayload.length % 4)) % 4),
        '=',
      );
      const parsedPayload = JSON.parse(atob(paddedPayload)) as { exp?: unknown };

      return typeof parsedPayload.exp === 'number' ? parsedPayload.exp * 1000 : null;
    } catch {
      return null;
    }
  }
}
